import type { BlockDataDTO, IconData } from '@next-space/fe-api-idl';
import { BlockType, PermissionRole, PermissionType } from '@next-space/fe-api-idl';
import { textToSegments } from 'src/editor/utils/editor';
import { createDocPage } from 'src/hooks/page';
import { getCurrentSpaceId, getCurrentSpaceView } from 'src/hooks/space/get-space';
import { transaction } from 'src/hooks/use-transaction';
import { TocType } from 'src/views/main/aside/toc/types';
import { removeBlock } from './remove';
import { updateBlock } from './update';
import { addBlock } from './add';
import { getState } from 'src/redux/store';
import { archiveBlock } from './archive';
import { setBlockPermission } from './update-permission';
import { useWikiStatusStore } from 'src/views/main/aside/wiki/use-wiki-status-store';
import { getRolePermissionRole } from '@flowus/common/block/permissions';
import { getPermissions } from 'src/hooks/share/use-permissions';

/** 知识库管理 */
export const WikiManager = {
  /**
   * @param icon 页面icon
   */
  createWiki: (name: string, description: string, icon?: IconData, isPrivate?: boolean) => {
    const wikiId = createDocPage(
      BlockType.WIKI,
      {
        parentId: getCurrentSpaceId(),
      },
      TocType.PRIVATE,
      textToSegments(name),
      textToSegments(description),
      icon
    );
    if (wikiId && !isPrivate) {
      // 创建个人知识库时，不需要公开分享isPrivate为true
      transaction(() => {
        setBlockPermission(wikiId, {
          role: PermissionRole.READER,
          type: PermissionType.PUBLIC,
        });
      });
    }
    return wikiId;
  },
  deleteWiki: (blockId: string) => {
    transaction(() => {
      removeBlock(blockId, false, true);
    });
  },
  updateWiki: (blockId: string, name: string, description: string, icon?: IconData) => {
    transaction(() => {
      updateBlock(blockId, {
        data: {
          segments: textToSegments(name),
          description: textToSegments(description),
          icon,
        },
      });
    });
  },

  addReferencePage: (wikiId: string, pageId: string) => {
    const exist = findAlreadyExistInWiki(wikiId, pageId);
    if (exist) {
      return false;
    }
    const personalWikiId = getCurrentSpaceView()?.setting.wikiId;
    if (wikiId !== personalWikiId) {
      WikiManager.openSharePermission(pageId);
    }
    transaction(() => {
      const newBlockId = addBlock(
        {
          type: BlockType.REFERENCE,
          data: {
            display: 'linkPage',
            ref: {
              uuid: pageId,
            },
          },
        },
        {
          parentId: wikiId,
        }
      );
      useWikiStatusStore.getState().setStatus(newBlockId, {
        progress: 0,
        status: 'pending',
        isError: false,
      });
    });

    return true;
  },
  openSharePermission: (pageId: string) => {
    const { role, shared } = getPermissions(pageId);
    const { editor } = getRolePermissionRole(role);
    if (!shared && editor) {
      transaction(() => {
        setBlockPermission(pageId, {
          role: PermissionRole.READER,
          type: PermissionType.PUBLIC,
        });
      });
    }
  },
  removeReferencePage: (wikiId: string, pageId: string) => {
    const exist = findAlreadyExistInWiki(wikiId, pageId);
    if (exist) {
      transaction(() => {
        removeBlock(exist);
      });
    }
    return true;
  },
  removeContent: (blockId: string) => {
    transaction(() => {
      archiveBlock(blockId);
    });
  },
  removeWiki: (wikiId: string) => {
    transaction(() => {
      archiveBlock(wikiId);
    });
  },
  addFileBlock: (
    wikiId: string,
    fileBlockId: string,
    data: Pick<BlockDataDTO, 'segments' | 'ossName' | 'size' | 'extName'>
  ) => {
    transaction(() => {
      addBlock(
        {
          uuid: fileBlockId,
          type: BlockType.FILE,
          data: {
            display: 'file',
            ...data,
          },
        },
        {
          parentId: wikiId,
        }
      );
      useWikiStatusStore.getState().setStatus(fileBlockId, {
        progress: 0,
        status: 'pending',
        isError: false,
      });
    });
  },
};

const findAlreadyExistInWiki = (wikiId: string, pageId: string) => {
  const state = getState();
  const wiki = state.blocks[wikiId];
  const found = wiki?.subNodes.find((v) => {
    const b = state.blocks[v];
    if (b?.type === BlockType.REFERENCE) {
      return b.data.ref?.uuid === pageId;
    }
    return false;
  });
  return found;
};
